import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  pushVX: number;
  pushVY: number;
  rot: number;
  baseX: number;
  baseY: number;
  timeOffset: number;
  width: number;
  height: number;
  el: HTMLElement;
}

@Component({
  selector: 'xware-login-background',
  templateUrl: './login-background.component.html',
  styleUrls: ['./login-background.component.scss'],
})
export class LoginBackgroundComponent implements AfterViewInit, OnDestroy {
  particlesVisible = false;

  @ViewChildren('imgParticle') imgParticles!: QueryList<ElementRef>;
  @ViewChild('containerRef') containerRef!: ElementRef;
  @ViewChild('loginCardWrapper') loginCardWrapperRef!: ElementRef;

  private particles: Particle[] = [];
  private animFrameId: number | null = null;
  private mouseX = -9999;
  private mouseY = -9999;
  private resizeObserver: ResizeObserver | null = null;

  private readonly OSCILLATION_RANGE = 6;
  private readonly ATTRACTION_RADIUS = 180;
  private readonly PUSH_RADIUS = 200;
  private readonly PUSH_FORCE = 10;
  private time = 0;

  ngAfterViewInit(): void {
    this.runLoop();
    setTimeout(() => (this.particlesVisible = true), 75);
    const container = this.containerRef?.nativeElement as HTMLElement;
    if (container) {
      this.resizeObserver = new ResizeObserver(() => {
        this.particles = [];
      });
      this.resizeObserver.observe(container);
    }
  }

  ngOnDestroy(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.resizeObserver?.disconnect();
  }

  onMouseMove(event: MouseEvent): void {
    const rect = (this.containerRef.nativeElement as HTMLElement).getBoundingClientRect();
    this.mouseX = event.clientX - rect.left;
    this.mouseY = event.clientY - rect.top;
  }

  onMouseLeave(): void {
    this.mouseX = -9999;
    this.mouseY = -9999;
  }

  onContainerClick(event: MouseEvent): void {
    const container = this.containerRef.nativeElement as HTMLElement;
    const rect = container.getBoundingClientRect();
    const cx = event.clientX - rect.left;
    const cy = event.clientY - rect.top;

    this.particles.forEach(p => {
      const dx = p.x + p.width / 2 - cx;
      const dy = p.y + p.height / 2 - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < this.PUSH_RADIUS && dist > 0) {
        const force = ((this.PUSH_RADIUS - dist) / this.PUSH_RADIUS) * this.PUSH_FORCE;
        p.pushVX = (dx / dist) * force;
        p.pushVY = (dy / dist) * force;
      }
    });
  }

  private initParticles(): void {
    const container = this.containerRef.nativeElement as HTMLElement;
    const loginCard = this.loginCardWrapperRef?.nativeElement as HTMLElement;
    if (!loginCard) return;

    const elements = this.imgParticles.toArray().map(ref => ref.nativeElement as HTMLElement);
    const dims = elements.map(el => ({ w: el.offsetWidth || 104, h: el.offsetHeight || 104 }));
    if (dims.length < 4) return;

    const wRect = container.getBoundingClientRect();
    const lRect = loginCard.getBoundingClientRect();
    const lLeft = lRect.left - wRect.left;
    const lTop = lRect.top - wRect.top;
    const lRight = lLeft + lRect.width;
    const lBottom = lTop + lRect.height;

    const positions = [
      { x: lLeft - dims[0].w / 2, y: lTop - dims[0].h / 2 },
      { x: lLeft - dims[1].w / 2 - 30, y: lTop + dims[0].h + dims[1].h / 2 - 40 },
      { x: lRight - dims[2].w / 2 + 30, y: lBottom - dims[3].h - dims[2].h / 2 },
      { x: lRight - dims[3].w / 2, y: lBottom - dims[3].h / 2 },
    ];

    this.imgParticles.forEach((ref, index) => {
      const el = ref.nativeElement as HTMLElement;
      const timeOffset = (index / 4) * Math.PI * 2;
      const { x, y } = positions[index] ?? { x: 0, y: 0 };
      const w = dims[index].w;
      const h = dims[index].h;
      this.particles.push({
        x,
        y,
        vx: 0,
        vy: 0,
        pushVX: 0,
        pushVY: 0,
        rot: 0,
        baseX: x,
        baseY: y,
        timeOffset,
        width: w,
        height: h,
        el,
      });
      el.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(0deg)`;
    });
  }

  private runLoop(): void {
    const step = () => {
      this.tick();
      this.animFrameId = requestAnimationFrame(step);
    };
    this.animFrameId = requestAnimationFrame(step);
  }

  private tick(): void {
    if (this.particles.length === 0) {
      const container = this.containerRef.nativeElement as HTMLElement;
      if (container.clientWidth > 0 && container.clientHeight > 0) {
        this.initParticles();
      }
      return;
    }

    this.time += 0.008;

    this.particles.forEach(p => {
      const targetX = p.baseX + Math.sin(this.time + p.timeOffset) * this.OSCILLATION_RANGE;
      const targetY = p.baseY + Math.cos(this.time + p.timeOffset * 0.7) * this.OSCILLATION_RANGE;

      const dx = this.mouseX - (p.x + p.width / 2);
      const dy = this.mouseY - (p.y + p.height / 2);
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < this.ATTRACTION_RADIUS && dist > 0) {
        const t = (this.ATTRACTION_RADIUS - dist) / this.ATTRACTION_RADIUS;
        p.vx += (dx / dist) * t * t * 0.12;
        p.vy += (dy / dist) * t * t * 0.12;
      }

      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      if (speed > 2.5) {
        p.vx = (p.vx / speed) * 2.5;
        p.vy = (p.vy / speed) * 2.5;
      }

      p.pushVX *= 0.92;
      p.pushVY *= 0.92;
      p.vx *= 0.88;
      p.vy *= 0.88;

      p.x += (targetX - p.x) * 0.05 + p.vx + p.pushVX;
      p.y += (targetY - p.y) * 0.05 + p.vy + p.pushVY;

      p.rot = Math.sin(this.time * 0.6 + p.timeOffset) * 3;
      p.el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) rotate(${p.rot}deg)`;
    });
  }
}
